import {
  CheckCircle as CheckIcon,
  Psychology as AnalyzerIcon,
  Splitscreen as SplitIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { Scene, EventSummary, AnySummary } from '@/lib/types';

interface ProposedScene {
  name: string;
  goal: string;
  description: string;
  eventIds: string[];
}

interface ProposedSplitDialogProps {
  open: boolean;
  onClose: () => void;
  proposal: NonNullable<Scene['proposal']> | undefined | null;
  analyzerLoading: boolean;
  onCancelProposal: () => Promise<void>;
  onApplySuggestions: () => Promise<void>;
  events: Record<string, EventSummary>;
  getEntityName: (id: string, entities: Record<string, AnySummary>) => string;
}

export function ProposedSplitDialog({
  open,
  onClose,
  proposal,
  analyzerLoading,
  onCancelProposal,
  onApplySuggestions,
  events,
  getEntityName
}: ProposedSplitDialogProps) {
  const { t } = useTranslation();

  if (!proposal) return null;

  const getVerdictIcon = (verdict: string) => {
    switch (verdict) {
      case 'pass': return <CheckIcon color="success" />;
      case 'suggest_split': return <SplitIcon color="primary" />;
      case 'needs_more_detail': return <WarningIcon color="warning" />;
      default: return null;
    }
  };

  const getVerdictSeverity = (verdict: string): 'success' | 'info' | 'warning' => {
    switch (verdict) {
      case 'pass': return 'success';
      case 'suggest_split': return 'info';
      case 'needs_more_detail': return 'warning';
      default: return 'info';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AnalyzerIcon color="primary" /> {t('entity_view.scenes.scene_analysis_result')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Alert severity={getVerdictSeverity(proposal.verdict)} icon={getVerdictIcon(proposal.verdict)}>
            <AlertTitle sx={{ textTransform: 'capitalize' }}>
              {t('entity_view.scenes.verdict')}: {t(`entity_view.scenes.verdicts.${proposal.verdict}`)}
            </AlertTitle>
            {proposal.analysis}
          </Alert>

          {proposal.proposedScenes && proposal.proposedScenes.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SplitIcon fontSize="small" /> {t('entity_view.scenes.proposed_split')}
              </Typography>
              <Stack spacing={2}>
                {proposal.proposedScenes.map((ps: ProposedScene, idx: number) => (
                  <Card key={idx} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" color="primary" fontWeight={600}>
                        {ps.name}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}><strong>{t('entity_view.scenes.goal')}:</strong> {ps.goal}</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>{ps.description}</Typography>
                      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="caption" sx={{ width: '100%', mb: 0.5, color: 'text.secondary', fontWeight: 700 }}>{t('entity_view.scenes.events')}:</Typography>
                        {ps.eventIds.map((eid: string) => (
                          <Chip key={eid} label={getEntityName(eid, events)} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        {proposal.verdict !== 'pass' && (
          <Button onClick={onCancelProposal} variant="text" color="error" disabled={analyzerLoading}>
            {t('entity_view.scenes.remove_result')}
          </Button>
        )}
        <Stack direction="row" spacing={2}>
          {proposal.verdict !== 'pass' && (
            <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
              {t('entity_view.scenes.decide_later')}
            </Button>
          )}
          {(proposal.verdict === 'suggest_split' || proposal.verdict === 'pass') && (
            <Button
              onClick={proposal.verdict === 'pass' ? onCancelProposal : onApplySuggestions}
              variant="contained"
              color="primary"
              startIcon={proposal.verdict === 'pass' ? <CheckIcon /> : <SplitIcon />}
              disabled={analyzerLoading}
              sx={{ borderRadius: 2 }}
            >
              {t('entity_view.scenes.accept')}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
